import { Component, DestroyRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { ButtonAddNewComponent } from '../../../../shared/components/button-add-new/button-add-new.component';
import { SidepanelService } from '../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../shared/components/toastr/toastr-success.component';
import { Caractéristique } from '../models/caractéristique.model';
import { CaracteristiqueCreateRequest } from '../requests/caractéristique-create-request';
import { CaractéristiqueStore } from '../store/caractéristique.store';
import { Référentiel } from '../../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-form-secteurs-activite',
  imports: [ReactiveFormsModule, HlmFieldImports, HlmInputImports, ButtonAddNewComponent, LabelRequiredDirective],
  templateUrl: './form-secteurs-activite.component.html',
})
export class FormSecteursActiviteComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #caracteristiqueStore = inject(CaractéristiqueStore);

  readonly referentiel = input.required<Référentiel>();
  readonly secteur = input.required<Caractéristique>();

  readonly form = this.#fb.nonNullable.group({
    secteurs: this.#fb.array([]),
  });

  ngOnInit(): void {
    this.addSecteur();

    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  addSecteur() {
    const secteur = this.#fb.nonNullable.group({
      nom: [null, Validators.required],
    });
    this.secteurs.push(secteur);
  }

  onSubmit() {
    const toastrId = uuidv7();

    const nouveauxSecteurs: CaracteristiqueCreateRequest = {
      idReferentiel: this.referentiel().id,
      idCaracteristiqueParente: this.secteur()?.id,
      caracteristiques: (this.secteurs.value as any[])
        .filter((x: { nom: string }) => !!x.nom?.trim())
        .map((x) => ({ nom: x.nom, estSecteur: true })),
    };

    if (!nouveauxSecteurs.caracteristiques || nouveauxSecteurs.caracteristiques.length === 0) {
      this.form.markAllAsTouched();
      return;
    }

    this.#caracteristiqueStore.créer({
      data: nouveauxSecteurs,
      next: () => {
        this.#caracteristiqueStore.charger(this.referentiel().id);
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: 'Création des secteurs réussie',
            id: toastrId,
          },
          id: toastrId,
        });
        this.#sidepanelService.close();
      },
      error: () => this.alreadyExists(nouveauxSecteurs.caracteristiques),
    });
  }

  alreadyExists(
    nouveauxLivrables: {
      nom: string;
    }[],
  ) {
    const secteurs = this.secteur().sousCaracteristiques;

    const doublonsAvecSecteursExistants = secteurs.filter((x) => nouveauxLivrables.map((y) => y.nom).includes(x.nom));

    this.secteurs.controls.forEach((control) => {
      if (
        doublonsAvecSecteursExistants.includes(control.value.nom) ||
        this.secteurs.controls.find((c) => c.value.nom === control.value.nom && c != control)
      ) {
        control.get('nom')?.setErrors({ notUnique: true });
      }
    });
  }

  get secteurs() {
    return this.form.get('secteurs') as FormArray;
  }
}
